import { ColumnDef } from '@tanstack/react-table'
import { FieldArrayWithId, UseFieldArrayRemove, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { TagsField } from 'components/Form'
import { LastModifiedCell } from 'components/Table'
import { PREDEFINED_AD_UNIT_SIZES } from 'features/globalSetup/features/viewport'
import { useAbility } from 'providers/casl'
import { Schema } from '../../../../../schema'
import { ActionsCell } from '../components/ActionsCell/ActionsCell'
import { ViewportWidthCell } from '../components/ViewportWidthCell'

type Props = {
  remove: UseFieldArrayRemove
}

export const useColumns = ({ remove }: Props): ColumnDef<FieldArrayWithId<Schema>>[] => {
  const { t } = useTranslation('features/adUnit', {
    keyPrefix: 'form.refresher.refreshRedirect.columns.refreshSizes.viewportList.header',
  })
  const { control } = useFormContext<Schema>()
  const ability = useAbility()
  const cannotUpdateAdUnit = ability.cannot('update', 'AdUnit')

  return [
    {
      accessorKey: 'viewport.name',
      cell: ({
        row: {
          original: {
            viewport: { name },
          },
        },
      }) => name,
      header: t('name'),
    },
    {
      accessorKey: 'viewport.minimumWidth',
      cell: ctx => <ViewportWidthCell {...ctx} />,
      header: t('viewportWidth'),
    },
    {
      accessorKey: 'additionalSizes',
      cell: ({ row: { index } }) => (
        <TagsField
          control={control}
          disabled={cannotUpdateAdUnit}
          name={`refreshRedirect.mediaTypeRefreshers.viewportAttributes.${index}.additionalSizes`}
          options={PREDEFINED_AD_UNIT_SIZES}
        />
      ),
      header: t('additionalSizes.title'),
      meta: {
        nonClickable: true,
        tooltip: {
          content: t('additionalSizes.tooltip.content'),
        },
      },
    },
    {
      accessorKey: 'removeSizes',
      cell: ({ row: { index } }) => (
        <TagsField
          control={control}
          disabled={cannotUpdateAdUnit}
          name={`refreshRedirect.mediaTypeRefreshers.viewportAttributes.${index}.removeSizes`}
          options={PREDEFINED_AD_UNIT_SIZES}
        />
      ),
      header: t('removeSizes.title'),
      meta: {
        nonClickable: true,
        tooltip: {
          content: t('removeSizes.tooltip.content'),
        },
      },
    },
    {
      accessorKey: 'updatedAt',
      cell: ctx => <LastModifiedCell {...ctx} />,
      header: t('updatedAt'),
    },
    {
      accessorKey: 'actions',
      cell: ({ row: { index } }) => <ActionsCell removeProps={{ index, remove }} />,
      header: t('actions'),
    },
  ]
}
